import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ControlBar,
  GridLayout,
  RoomAudioRenderer,
  useTracks,
  RoomContext,
  LiveKitRoom,
  useParticipants,
  ParticipantTile,
} from "@livekit/components-react";
import { Room, Track } from "livekit-client";
import "@livekit/components-styles";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";

import LiveClassLoader from "components/loader/LiveClassLoader";
import LiveClassRecorder from "./LiveClassRecorder";
import ScreenShareView from "./ScreenShareView"; // Use the enhanced responsive version
import {
  checkUserPartOfThisLiveClass,
  getLivekitToken,
} from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";
import {
  liveKitServerUrl,
} from "constants/Constants";

const VideoConference = () => {
  const [isRoomCreated, setIsRoomCreated] = useState(false);
  const [cookies] = useCookies();
  const { roomId } = useParams();
  const [showLoader, setShowLoader] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const location = useLocation();

  const serverUrl = useMemo(() => liveKitServerUrl, []);

  // Initialize room with optimization settings
  const [room] = useState(
    () =>
      new Room({
        adaptiveStream: true, // Optimize video quality for each participant's screen
        dynacast: true, // Enable automatic audio/video quality optimization
      })
  );

  // Connect to LiveKit room
  const connectToRoom = useCallback(async (identity = cookies?.name) => {
    try {
      const payload = { identity, room: roomId };
      const response = await getLivekitToken("", payload);
      const token = response?.data?.token;

      if (!token) {
        throw new Error('Failed to get LiveKit token');
      }

      await room.connect(serverUrl, token);
      setIsRoomCreated(true);
      setShowLoader(false);
    } catch (error) {
      console.error('Error connecting to room:', error);
      showToast("Failed to connect to live class", "error");
      navigate("/live-class");
    }
  }, [room, serverUrl, roomId, cookies?.name, showToast, navigate]);

  // Verify user access to live class
  const verifyUserAccess = useCallback(async () => {
    try {
      const payload = {
        user: cookies?.userId,
        liveClassId: roomId,
      };
      
      const response = await checkUserPartOfThisLiveClass("", payload);
      
      if (response?.data?.status) {
        await connectToRoom();
      } else {
        showToast("You are not part of this live class", "error");
        navigate("/live-class");
      }
    } catch (error) {
      console.error('Error verifying user access:', error);
      showToast("Error verifying access to live class", "error");
      navigate("/live-class");
    }
  }, [cookies?.userId, roomId, connectToRoom, showToast, navigate]);

  // Handle whiteboard opening
  // const openWhiteboard = useCallback(() => {
  //   window.open(
  //     `${appBaseUrl}whiteboard`,
  //     "_blank",
  //     "noopener,noreferrer"
  //   );
  // }, []);
//Open Whiteborad i new tab
  const openWhiteboard = () => {
  const baseUrl = window.location.origin; 
  window.open(`${baseUrl}/whiteboard`, '_blank', 'noopener,noreferrer');
};

  // Handle room disconnect
  useEffect(() => {
    const handleDisconnect = () => {
      navigate("/live-class");
    };

    room?.on("disconnected", handleDisconnect);
    return () => room?.off("disconnected", handleDisconnect);
  }, [room, navigate]);

  // Validate room ID and verify access
  useEffect(() => {
    if (!roomId) {
      navigate(-1);
      return;
    }
    verifyUserAccess();
  }, [roomId, verifyUserAccess, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  return (
    <>
      <LiveClassLoader showLoader={showLoader} />
      
      <RoomContext.Provider value={room}>
        <div data-lk-theme="default">
          <VideoConferenceLayout />
          <RoomAudioRenderer />
          
          {/* Control bar with additional features */}
          <div 
            className="d-flex justify-content-center align-items-center flex-wrap"
            style={{
              position: "fixed",
              bottom: "0px",
              left: "0",
              right: "0",
              width: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              backdropFilter: "blur(10px)",
              padding: "12px 8px",
              zIndex: 1000,
              gap: "8px",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.3)"
            }}
          >
            <div className="d-flex align-items-center">
              <ControlBar />
            </div>
            <Button 
              onClick={openWhiteboard} 
              variant="outline-light"
              size="sm"
              className="mx-1 d-flex align-items-center"
              style={{ 
                minWidth: "auto",
                fontSize: "0.875rem",
                padding: "0.5rem 1rem",
                borderRadius: "20px",
                transition: "all 0.2s ease"
              }}
            >
              <span className="d-none d-md-inline">🖊️ Whiteboard</span>
              <span className="d-md-none">🖊️ WB</span>
            </Button>
            <div className="d-flex align-items-center">
              <LiveClassRecorder room={room} />
            </div>
          </div>
        </div>
      </RoomContext.Provider>
    </>
  );
};

// Separated video conference layout component
const VideoConferenceLayout = () => {
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );

  const participants = useParticipants();
  
  const screenShareTracks = useTracks([{ source: Track.Source.ScreenShare }], {
    onlySubscribed: false,
  });

  const screenShareTrack = screenShareTracks[0];

  // If someone is sharing screen, show screen share view
  if (screenShareTrack) {
    return (
      <ScreenShareView 
        screenShareTrack={screenShareTrack}
        participants={participants}
        cameraTracks={cameraTracks}
      />
    );
  }

  // Default grid layout
  return (
    <GridLayout
      tracks={cameraTracks}
      style={{
        height: "calc(100vh - 80px)", // Account for fixed control bar
        backgroundColor: "#000",
      }}
    >
      <ParticipantTile />
    </GridLayout>
  );
};

export default VideoConference;